import { FC, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CommunityFeed } from '@/components/community/CommunityFeed';
import { CommunityProfile } from '@/components/community/CommunityProfile';
import { Send, Smile, Users } from 'lucide-react';
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react';
import { Separator } from '@/components/ui/separator';
import { motion } from 'framer-motion';

const Community: FC = () => {
  const [newPost, setNewPost] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { toast } = useToast();
  const feedRef = useRef<{ refetch: () => Promise<void> }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPost.trim() || isPosting) return;

    setIsPosting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('community_posts')
        .insert({ content: newPost.trim(), user_id: user.id });

      if (error) throw error;

      setNewPost('');
      toast({
        title: '¡Publicación exitosa!',
        description: 'Tu mensaje ha sido publicado en la comunidad.',
      });

      // Actualizar el feed
      await feedRef.current?.refetch();

    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear la publicación. Por favor, intenta nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8">
        {/* Título de la página */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-6"
        >
          <Users className="w-8 h-8 text-primary animate-pulse" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Nuestra Comunidad
          </h1>
        </motion.div>

        <div className="space-y-8">
          {/* Perfil y nueva publicación */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Perfil */}
              <div className="w-full lg:w-[300px] xl:w-[350px]">
                <CommunityProfile />
              </div>

              {/* Nueva publicación */}
              <div className="flex-grow">
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <Textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="Comparte algo con la comunidad..."
                    className="min-h-[100px] sm:min-h-[120px] resize-none p-3 w-full text-sm sm:text-base rounded-lg border border-gray-200"
                  />
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShowEmojiPicker(prev => !prev)}
                      className="h-10 w-10 text-primary hover:text-primary hover:bg-primary/10 transition-colors"
                    >
                      <Smile className="h-5 w-5" />
                    </Button>
                    <Button 
                      type="submit"
                      disabled={isPosting || !newPost.trim()}
                      className="bg-primary hover:bg-primary/90"
                    >
                      {isPosting ? (
                        <span>Publicando...</span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Publicar
                          <Send className="h-4 w-4" />
                        </span>
                      )}
                    </Button>
                  </div>
                </form>
                
                {showEmojiPicker && (
                  <div className="absolute z-50 mt-2">
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                      <Picker
                        data={data}
                        onEmojiSelect={(emoji: any) => {
                          setNewPost(prev => prev + emoji.native);
                          setShowEmojiPicker(false);
                        }}
                        locale="es"
                        theme="light"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Feed */}
          <div className="space-y-6">
            <CommunityFeed ref={feedRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;